import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Check } from "lucide-react";
import { HandshakeIcon } from "../lib/icons";
import { postGrantThankYou } from "../lib/payouts";

const MAX = 200;

/** One-line thank-you the student pins to their campaign wall after a grant lands. */
export const ThankYouComposer = ({ grant, onPosted }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const remaining = MAX - text.length;

  if (grant.thank_you) {
    return (
      <p className="mt-2 flex items-start gap-1.5 text-[13px] text-grass" data-testid={`grant-thankyou-posted-${grant.id}`}>
        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />
        <span>On your wall: <span className="italic text-ink/80">“{grant.thank_you}”</span></span>
      </p>
    );
  }
  if (!grant.can_thank) return null;

  const submit = async () => {
    const trimmed = text.trim();
    if (!trimmed || remaining < 0) return;
    setBusy(true);
    try {
      const updated = await postGrantThankYou(grant.id, trimmed);
      toast.success("Posted to your campaign wall.", { description: "It's pinned at the top so your supporters see it first." });
      onPosted?.(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't post that — try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 rounded-md border border-grass/30 bg-grass/[0.05] p-3" data-testid={`grant-thankyou-composer-${grant.id}`}>
      <p className="flex items-center gap-1.5 text-[13px] font-semibold uppercase tracking-[0.18em] text-grass">
        <HandshakeIcon className="h-3.5 w-3.5" /> Say thanks on your campaign wall
      </p>
      <div className="mt-2 flex items-center gap-2">
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          maxLength={MAX + 20}
          placeholder="One line — e.g. “Housing deposit paid. Thank you for getting me to Kyoto.”"
          data-testid={`grant-thankyou-input-${grant.id}`}
          className="min-w-0 flex-1 rounded-md border border-border bg-bone px-3 py-2 text-base text-ink placeholder:text-ink/30 focus:border-ink focus:outline-none"
        />
        <span className={`text-[13px] tabular-nums ${remaining < 0 ? "font-semibold text-scarlet" : "text-olive/70"}`}>{remaining}</span>
        <button
          type="button"
          onClick={submit}
          disabled={busy || !text.trim() || remaining < 0}
          data-testid={`grant-thankyou-submit-${grant.id}`}
          className="inline-flex shrink-0 items-center gap-1 rounded-full bg-ink px-3.5 py-2 text-[13px] font-semibold text-sand transition-colors hover:bg-forest disabled:opacity-40"
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : null} Post
        </button>
      </div>
    </div>
  );
};
